import React, { useState } from 'react';
import { ContactDto, InvitationDto } from '../../types/settings';
import { removeContact, createInvitation, updateInvitationStatus } from '../../api/settingsApi';
import { Users, Mail, Trash2, Check, X, Clock, Send, List } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useDialog } from '../../contexts/DialogContext';
import { useTodoLists } from '../../hooks/useTodos';
import { Checkbox } from '@mantine/core';

interface ContactManagementProps {
  contacts: ContactDto[];
  sentInvitations: InvitationDto[];
  receivedInvitations: InvitationDto[];
  onDataUpdate: () => void;
}

export const ContactManagement: React.FC<ContactManagementProps> = ({
  contacts,
  sentInvitations,
  receivedInvitations,
  onDataUpdate
}) => {
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteName, setNewInviteName] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  
  const { confirmDelete, alert } = useDialog();
  const { data: todoLists = [] } = useTodoLists();

  const showMessage = (message: string) => {
    setStatusMessage(message);
    setTimeout(() => setStatusMessage(null), 3000);
  };

  const handleListToggle = (listId: number) => {
    setSelectedListIds(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId]
    );
  };

  const handleRemoveContact = async (contactId: number, contactName: string) => {
    confirmDelete(
      'Remove Contact',
      `Are you sure you want to remove ${contactName} from your contacts?`,
      async () => {
        try {
          await removeContact(contactId);
          showMessage('Contact removed successfully! All shared access has been revoked.');
          onDataUpdate();
        } catch {
          alert('error', 'Error', 'Failed to remove contact. Please try again.');
        }
      },
      [
        'Remove them from any shared lists',
        'Remove task assignments between you',
        'Cancel any pending invitations',
        'This action cannot be undone'
      ]
    );
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInviteEmail.trim() || !newInviteName.trim()) return;

    try {
      setIsSendingInvite(true);
      // TODO: Update API call to include selectedListIds when backend is updated
      await createInvitation({
        inviteeEmail: newInviteEmail.trim(),
        inviteeName: newInviteName.trim(),
        message: inviteMessage.trim() || undefined,
        selectedListIds: selectedListIds
      });
      setNewInviteEmail('');
      setNewInviteName('');
      setInviteMessage('');
      setSelectedListIds([]);
      showMessage('Invitation sent successfully!');
      onDataUpdate();
    } catch {
      alert('error', 'Error', 'Failed to send invitation. Please try again.');
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleInvitationResponse = async (invitationId: number, status: 'accepted' | 'declined') => {
    try {
      await updateInvitationStatus(invitationId, status);
      showMessage(`Invitation ${status} successfully!`);
      onDataUpdate();
    } catch {
      alert('error', 'Error', `Failed to ${status === 'accepted' ? 'accept' : 'decline'} invitation.`);
    }
  };

  const handleCancelInvitation = async (invitationId: number) => {
    confirmDelete(
      'Cancel Invitation',
      'Are you sure you want to cancel this invitation?',
      async () => {
        try {
          await updateInvitationStatus(invitationId, 'cancelled');
          showMessage('Invitation cancelled successfully!');
          onDataUpdate();
        } catch {
          alert('error', 'Error', 'Failed to cancel invitation.');
        }
      }
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock size={16} className="status-pending" />;
      case 'accepted': return <Check size={16} className="status-accepted" />;
      case 'declined': return <X size={16} className="status-declined" />;
      case 'cancelled': return <X size={16} className="status-cancelled" />;
      default: return null;
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h2 className="settings-section-title">Contacts & Invitations</h2>
        <p className="settings-section-description">
          Manage your contacts and send invitations to collaborate
        </p>
      </div>

      {statusMessage && (
        <div style={{
          padding: '12px 16px',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '6px',
          color: '#0369a1',
          marginBottom: '24px',
          fontSize: '14px'
        }}>
          {statusMessage}
        </div>
      )}

      {/* Contacts List */}
      <div className="settings-group">
        <h3 className="settings-group-title">
          <Users size={18} style={{ marginRight: '8px' }} />
          Your Contacts ({contacts.length})
        </h3>
        
        {contacts.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px', 
            color: '#6b7280',
            fontSize: '14px'
          }}>
            No contacts yet. Send invitations to connect with other users.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {contacts.map((contact) => (
              <div
                key={contact.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid var(--app-border)',
                  borderRadius: '6px',
                  background: 'var(--app-surface)'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '14px', color: 'var(--app-text-primary)' }}>
                    {contact.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--app-text-secondary)' }}>
                    {contact.email}
                    {contact.isOnline && (
                      <span style={{ 
                        marginLeft: '8px', 
                        color: '#10b981',
                        fontWeight: '500'
                      }}>
                        • Online
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveContact(contact.id, contact.name)}
                  className="settings-button danger"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send Invitation */}
      <div className="settings-group">
        <h3 className="settings-group-title">
          <Send size={18} style={{ marginRight: '8px' }} />
          Send Invitation
        </h3>
        
        <form onSubmit={handleSendInvitation} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Email address"
              value={newInviteEmail}
              onChange={(e) => setNewInviteEmail(e.target.value)}
              className="settings-input"
              required
              style={{ flex: '1', minWidth: '200px' }}
            />
            <input
              type="text"
              placeholder="Their name"
              value={newInviteName}
              onChange={(e) => setNewInviteName(e.target.value)}
              className="settings-input"
              required
              style={{ flex: '1', minWidth: '150px' }}
            />
          </div>
          <textarea
            placeholder="Personal message (optional)"
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--app-border)',
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical',
              minHeight: '60px',
              background: 'var(--app-surface)',
              color: 'var(--app-text-primary)'
            }}
          />
          
          {/* List Selection Subsection */}
          {todoLists.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                marginBottom: '12px',
                color: 'var(--app-text-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <List size={16} />
                Also add the user to the following lists
              </h4>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                maxHeight: '150px',
                overflowY: 'auto',
                padding: '8px',
                border: '1px solid var(--app-border)',
                borderRadius: '6px',
                background: 'var(--app-surface)'
              }}>
                {todoLists.map((list) => (
                  <label
                    key={list.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--app-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Checkbox
                      checked={selectedListIds.includes(list.id)}
                      onChange={() => handleListToggle(list.id)}
                      size="sm"
                    />
                    <div style={{
                      width: '16px',
                      height: '16px',
                      borderRadius: '3px',
                      backgroundColor: list.color || '#3b82f6',
                      flexShrink: 0
                    }} />
                    <span style={{ 
                      fontSize: '13px', 
                      color: 'var(--app-text-primary)',
                      fontWeight: '500'
                    }}>
                      {list.name}
                    </span>
                    {list.isShared && (
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--app-text-secondary)',
                        opacity: 0.8,
                        fontStyle: 'italic'
                      }}>
                        (shared)
                      </span>
                    )}
                  </label>
                ))}
              </div>
              {selectedListIds.length > 0 && (
                <p style={{
                  fontSize: '12px',
                  color: 'var(--app-text-secondary)',
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  {selectedListIds.length} list{selectedListIds.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}
          
          <button
            type="submit"
            disabled={isSendingInvite}
            className="settings-button primary"
            style={{ alignSelf: 'flex-start', marginTop: '16px' }}
          >
            {isSendingInvite ? 'Sending...' : 'Send Invitation'}
          </button>
        </form>
      </div>

      {/* Received Invitations */}
      {receivedInvitations.length > 0 && (
        <div className="settings-group">
          <h3 className="settings-group-title">
            <Mail size={18} style={{ marginRight: '8px' }} />
            Received Invitations ({receivedInvitations.length})
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {receivedInvitations.map((invitation) => (
              <div
                key={invitation.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid var(--app-border)',
                  borderRadius: '6px',
                  background: invitation.status === 'pending' ? 'var(--app-hover)' : 'var(--app-surface)'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '14px', color: 'var(--app-text-primary)' }}>
                    From: {invitation.inviterName}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--app-text-secondary)' }}>
                    {invitation.inviterEmail} • {formatDistanceToNow(new Date(invitation.createdAt))} ago
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(invitation.status)}
                  {invitation.status === 'pending' ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleInvitationResponse(invitation.id, 'accepted')}
                        className="settings-button primary"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        Accept
                      </button>
                      <button
                        onClick={() => handleInvitationResponse(invitation.id, 'declined')}
                        className="settings-button"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        Decline
                      </button>
                    </div>
                  ) : (
                    <span style={{ 
                      fontSize: '12px', 
                      textTransform: 'capitalize',
                      color: invitation.status === 'accepted' ? '#10b981' : '#6b7280'
                    }}>
                      {invitation.status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sent Invitations */}
      {sentInvitations.length > 0 && (
        <div className="settings-group">
          <h3 className="settings-group-title">
            Sent Invitations ({sentInvitations.length})
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sentInvitations.map((invitation) => (
              <div
                key={invitation.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px',
                  border: '1px solid var(--app-border)',
                  borderRadius: '6px',
                  background: 'var(--app-surface)'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '14px', color: 'var(--app-text-primary)' }}>
                    To: {invitation.inviteeEmail}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--app-text-secondary)' }}>
                    Sent {formatDistanceToNow(new Date(invitation.createdAt))} ago
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {getStatusIcon(invitation.status)}
                  <span style={{ 
                    fontSize: '12px', 
                    textTransform: 'capitalize',
                    color: invitation.status === 'accepted' ? '#10b981' : 
                           invitation.status === 'pending' ? '#f59e0b' : '#6b7280'
                  }}>
                    {invitation.status}
                  </span>
                  {invitation.status === 'pending' && (
                    <button
                      onClick={() => handleCancelInvitation(invitation.id)}
                      className="settings-button danger"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        .status-pending { color: #f59e0b; }
        .status-accepted { color: #10b981; }
        .status-declined { color: #ef4444; }
        .status-cancelled { color: #6b7280; }
      `}</style>
    </div>
  );
};
